import { useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ThisDocumentImages from 'components/editor/ImageLibrary/ThisDocumentImages';
import { ImageSelectContext } from 'components/editor/ImageLibrary/ImageSelectContext';
import { LibraryImages } from 'components/library/images/LibraryImages';
import ChatBotSpaceHolder from '../../component/ChatBotSpaceHolder';
import { DraggableImage } from 'components/editor/ImageLibrary/DraggableImage';
import { useReplaceImageBlock } from 'components/editor/hooks/useReplaceImageBlock';
import useClickToAddImage from 'components/editor/hooks/useClickToAddImage';
import { selectIsReplaceModeActive } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import type { RootState } from 'components/editor/grid/reduxStore/Store';

const HEADING_AND_TAB_HEIGHT = 149;
const TabPanel = ({ children, isOpen, index }: { children?: React.ReactNode; index: number; isOpen: boolean }) => {
  return (
    <div role="tabpanel" hidden={!isOpen} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} style={{ height: '100%' }}>
      {isOpen && (
        <Box sx={{ maxHeight: `calc(100% - ${HEADING_AND_TAB_HEIGHT}px)`, overflowY: 'auto' }}>
          {children}
          <ChatBotSpaceHolder />
        </Box>
      )}
    </div>
  );
};

export const ImagesUploadsPanel = () => {
  const [currentTab, setCurrentTab] = useState<number>(0);
  const { replaceImage } = useReplaceImageBlock();
  const isReplaceModeActive = useSelector((state: RootState) => selectIsReplaceModeActive(state));

  // Keep a ref to always call the latest replaceImage without changing the stable callback identity.
  // This prevents DraggableImage from re-rendering (and re-running useDrag) when replaceImage
  // is recreated due to Redux state updates during drag operations.
  const replaceImageRef = useRef(replaceImage);
  replaceImageRef.current = replaceImage;
  const stableOnSelect = useCallback((src: string) => replaceImageRef.current(src), []);

  const clickToAddImage = useClickToAddImage();
  const clickToAddImageRef = useRef(clickToAddImage);
  clickToAddImageRef.current = clickToAddImage;
  const stableOnClickToAdd = useCallback((src: string) => clickToAddImageRef.current(src), []);

  const imageSelectValue = useMemo(
    () => ({ onSelect: isReplaceModeActive ? stableOnSelect : stableOnClickToAdd }),
    [isReplaceModeActive, stableOnSelect, stableOnClickToAdd]
  );

  return (
    <ImageSelectContext.Provider value={imageSelectValue}>
      <Stack direction="column" gap={3} overflow="hidden" pb={6}>
        <Tabs
          TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{
            '&.MuiTabs-root': {
              minHeight: '2.625rem',
              height: '2.625rem',
              width: '100%',
            },
          }}
        >
          <Tab
            label={
              <Typography variant="labelMedium" textTransform="none" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
                This document
              </Typography>
            }
            value={0}
            sx={{
              width: '50%',
            }}
          />
          <Tab
            label={
              <Typography variant="labelMedium" textTransform="none" textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
                Your library
              </Typography>
            }
            value={1}
            sx={{
              width: '50%',
            }}
          />
        </Tabs>

        <TabPanel isOpen={currentTab === 0} index={0}>
          <ThisDocumentImages />
        </TabPanel>

        <TabPanel isOpen={currentTab === 1} index={1}>
          <LibraryImages DraggableImage={DraggableImage} />
        </TabPanel>
      </Stack>
    </ImageSelectContext.Provider>
  );
};
